/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Home` for the Caintra Connect frontend.
 * @module pages/Home
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "../styles/Home.module.css";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import FeaturedBanners from "../components/FeaturedBanners";
import PartnersImage from "../components/PartnersImage";
import EventsList from "../components/EventsList";
import RequirementsList from "../components/RequirementsList";
import HomeCalendar from "../components/HomeCalendar";

import { use, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import useSessionUserRefresh from "../hooks/useSessionUserRefresh";

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = use(AppContext);
  const [currentUser, setCurrentUser] = useState(null);

  useSessionUserRefresh(setCurrentUser, {
    refreshPermissions: true,
    refreshOnMount: true,
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{t("pages.home.title")}</h1>
          </div>
        </div>
      </div>

      {/* Featured Banners Section */}
      <div className={styles.fullWidthContainer}>
        <FeaturedBanners permissions={permissions} currentUser={currentUser} />
      </div>

      {/* Home Calendar Section */}
      <div className={styles.fullWidthContainer}>
        <HomeCalendar />
      </div>

      {/* Partners Image Section */}
      <div className={styles.fullWidthContainer}>
        <PartnersImage permissions={permissions} currentUser={currentUser} />
      </div>

      {/* Events and Requirements Section */}
      <div className={styles.homeContentContainer}>
        <div className={styles.contentRow}>
          <div className={styles.leftColumn}>
            <EventsList />
          </div>
          <div className={styles.rightColumn}>
            <RequirementsList />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
